import { NoConnectionError } from "../api/errors";

// Structured suggestion payload returned by the server-side phone-number
// validator (#284). Server returns these alongside the user-facing message
// when a typo is recognised (e.g. double country code -> suggest the
// stripped number). All fields optional - only `suggestedE164` is required
// to render the "Use this number" affordance.
export type PhoneValidationSuggestion = {
  rejectionReason?: string;
  code?: string;
  suggestedE164?: string;
  suggestedCallingCode?: string;
  suggestedPhoneNumber?: string;
};

// Masks the middle digits of an E.164 number for telemetry. Keeps the
// first 4 chars (`+CC` and one subscriber digit) and the last 2 so the
// destination is roughly identifiable without leaking the full number.
// Used by the [phone-validation] log events on rejection / apply.
export const maskE164 = (e164: string): string =>
  e164.length <= 6 ? e164 : `${e164.slice(0, 4)}***${e164.slice(-2)}`;

// Pulls the response body our API client (src/api/client.ts) stashed on a thrown error, if any.
// ValidationError stores it on `_error` (web's api/errors.ts).
function getResponseData(error: unknown): unknown {
  if (!error || typeof error !== "object") return undefined;
  const e = error as Record<string, unknown>;
  return e._error;
}

// Reads a string field off an unknown payload. Returns trimmed value or undefined.
function readString(obj: unknown, key: string): string | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;
}

// Extracts a phone-validation suggestion from any thrown error if the
// server returned one. Returns null when no `suggestedE164` is present -
// without that we can't render the "Use this number" button.
export function getPhoneValidationSuggestion(
  error: unknown,
): PhoneValidationSuggestion | null {
  const candidates: unknown[] = [getResponseData(error), error];
  for (const source of candidates) {
    const suggestedE164 = readString(source, "suggestedE164");
    if (!suggestedE164) continue;
    return {
      suggestedE164,
      suggestedCallingCode: readString(source, "suggestedCallingCode"),
      suggestedPhoneNumber: readString(source, "suggestedPhoneNumber"),
      rejectionReason: readString(source, "rejectionReason"),
      code: readString(source, "code"),
    };
  }
  return null;
}

export function getUserMessage(error: unknown, context?: string): string {
  // If #121's improved ValidationError message comes through as a string, use it directly
  if (typeof error === "string") return error;

  // A connection failure is diagnosable — never blame the user's input
  // (or rate limits) for it. The class carries customer-ready copy.
  if (error instanceof NoConnectionError) {
    return error.message;
  }

  // ValidationError from our API client (src/api/errors.ts)
  if (error && typeof error === "object" && "_error" in error) {
    const inner = (error as any)._error;

    // If the API sent a user-friendly message (from #121), pass it through.
    if (
      typeof inner === "string" &&
      inner.length > 0 &&
      !inner.startsWith("Validation Error")
    ) {
      return inner;
    }

    // Structured payload from #284's phone validator: read the user-facing
    // field directly off the response body. Without this we'd fall through
    // to ValidationError.toString() which `Object.values().join('. ')`s the
    // whole payload and produces a garbled string.
    const fromPayload =
      readString(inner, "userMessage") ||
      readString(inner, "error") ||
      readString(inner, "message");
    if (fromPayload) return fromPayload;

    // ValidationError.toString() strips the "Validation Error:" prefix,
    // so Lookup block messages (e.g. "Landline numbers cannot receive SMS...")
    // come through as clean, actionable text for the user (#250).
    if (typeof (error as any).toString === "function") {
      const cleaned = (error as any).toString();
      if (
        typeof cleaned === "string" &&
        cleaned.length > 0 &&
        !cleaned.startsWith("Validation Error") &&
        !cleaned.startsWith("Validation error") &&
        cleaned !== "[object Object]"
      ) {
        return cleaned;
      }
    }
    // Otherwise translate by context
    if (context === "phone-send") {
      return "We couldn't send your verification code. You may have requested too many codes \u2014 please wait a moment and try again.";
    }
    if (context === "phone-verify") {
      return "That code wasn't right. Please check and try again.";
    }
    return inner?.toString?.() || "Something went wrong. Please try again.";
  }

  // Network/fetch errors
  if (error instanceof TypeError && error.message?.includes("fetch")) {
    return "Please check your internet connection and try again.";
  }

  // Generic Error
  if (error instanceof Error) {
    // Pass through if it looks like a user-friendly message from the API
    if (
      error.message &&
      !error.message.startsWith("Error") &&
      error.message.length > 10
    ) {
      return error.message;
    }
    if (context === "phone-send") {
      return "Something went wrong sending your code. Please try again in a moment.";
    }
    if (context === "phone-verify") {
      return "Something went wrong verifying your code. Please try again.";
    }
  }

  return "Something went wrong. Please try again, or contact us for help.";
}
